import { useState, useMemo } from 'react'
import Button from '../../components/atoms/Buttons'
import FormInput from '../../components/atoms/Input/Form-Input'
import { useForm, FormProvider } from 'react-hook-form'
import { createToast } from '../../utils/Toast'
import { FaUserPlus, FaUserShield, FaUser, FaEdit } from 'react-icons/fa'
import ModalComponent from '../../components/atoms/Modal/ModalComponent/ModalComponent'
import { useUserContext } from '../../context/UserContext'
import { useMutation, useQuery } from '@apollo/client'
import { CREATE_MANUFACTURER } from '../../apollo/mutations/createManufacture'
import { UPDATE_USER } from '../../apollo/mutations/updateUser'
import { LIST_MANUFACTURERS_FULL } from '../../apollo/queries/listManufacturers'
import { DISABLE_MEMBER_USER } from '../../apollo/mutations/disableMemeber'
import { ENABLE_MEMBER_USER } from '../../apollo/mutations/enableMemeber'
import { BeatLoader } from 'react-spinners'

const Toast = createToast()

const CustomToggle = ({ checked, onChange, disabled }) => (
    <div
        onClick={!disabled ? onChange : undefined}
        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${checked ? 'bg-green-500' : 'bg-gray-300'}`}
    >
        <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </div>
)

function ManufacturerUsers({ manufacturer }) {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState(null)
    const [selectedUserType, setSelectedUserType] = useState('Member')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [optimisticToggles, setOptimisticToggles] = useState({})


    const { userData, isSaddlefitAdmin } = useUserContext()

    const { data: usersData, loading: usersLoading, error: usersError } = useQuery(LIST_MANUFACTURERS_FULL)

    const teamMembers = useMemo(() => {
        if (!usersData?.listManufacturers) return []

        return usersData.listManufacturers
            .filter(u => u.manufacturerObject?.brand?.toLowerCase() === manufacturer.manufacturerObject?.brand?.toLowerCase())
            .filter(u => u.email?.toLowerCase() !== userData?.email?.toLowerCase())
            .map(u => {
                const isEnable = optimisticToggles[u.id] !== undefined ? optimisticToggles[u.id] : (u.isEnable ?? true)
                return {
                    ...u,
                    isEnable,
                    role: u.id === manufacturer.id ? 'Admin' : 'Member',
                    status: isEnable === false ? 'Disabled' : 'Active',
                    isActive: isEnable !== false
                }
            })
            .sort((a, b) => (a.role === 'Admin' ? -1 : b.role === 'Admin' ? 1 : 0))
    }, [usersData, manufacturer, userData?.email, optimisticToggles])

    const [createManufacturer] = useMutation(CREATE_MANUFACTURER)
    const [updateUser] = useMutation(UPDATE_USER)
    const [disableMemberUser] = useMutation(DISABLE_MEMBER_USER)
    const [enableMemberUser] = useMutation(ENABLE_MEMBER_USER)

    const [activeIndex, setActiveIndex] = useState(null)

    const methods = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            role: 'Member'
        }
    })

    const { handleSubmit, reset, setValue } = methods

    const onOpenModal = (user = null) => {
        if (user) {
            setIsEditMode(true)
            setSelectedUserId(user.id)
            setSelectedUserType(user.type || 'Member')
            setValue('firstName', user.firstName)
            setValue('lastName', user.lastName)
            setValue('email', user.email)
            setValue('role', user.role)
        } else {
            setIsEditMode(false)
            setSelectedUserId(null)
            setSelectedUserType('Member')
            reset({
                firstName: '',
                lastName: '',
                email: '',
                role: 'Member'
            })
        }
        setIsModalOpen(true)
    }

    const onCloseModal = () => {
        setIsModalOpen(false)
        setIsEditMode(false)
        setSelectedUserId(null)
        reset()
    }

    const onSubmit = async (data) => {
        setSubmitLoading(true)
        try {
            if (isEditMode) {
                await updateUser({
                    variables: {
                        input: {
                            id: selectedUserId,
                            firstName: data.firstName,
                            lastName: data.lastName,
                            type: selectedUserType,
                            brand: manufacturer.manufacturerObject?.brand
                        }
                    },
                    refetchQueries: [{ query: LIST_MANUFACTURERS_FULL }]
                })
                Toast.fire({
                    icon: 'success',
                    title: 'Member updated successfully!',
                })
            } else {
                await createManufacturer({
                    variables: {
                        input: {
                            email: data.email,
                            firstName: data.firstName,
                            lastName: data.lastName,
                            brandName: manufacturer.manufacturerObject?.brand,
                            type: 'Member'
                        }
                    },
                    refetchQueries: [{ query: LIST_MANUFACTURERS_FULL }]
                })
                Toast.fire({
                    icon: 'success',
                    title: 'Member invited successfully!',
                })
            }
            onCloseModal()
        } catch (error) {
            console.error('Error saving user:', error)
            Toast.fire({ icon: 'error', title: error.message || `Failed to ${isEditMode ? 'update' : 'add'} user` })
        } finally {
            setSubmitLoading(false)
        }
    }

    const handleToggleUserStatus = async (user) => {
        const targetEnable = !user.isEnable

        setOptimisticToggles(prev => ({ ...prev, [user.id]: targetEnable }))

        try {
            if (targetEnable) {
                await enableMemberUser({
                    variables: { id: user.id },
                    refetchQueries: [{ query: LIST_MANUFACTURERS_FULL }]
                })
            } else {
                await disableMemberUser({
                    variables: { id: user.id },
                    refetchQueries: [{ query: LIST_MANUFACTURERS_FULL }]
                })
            }

            Toast.fire({
                icon: 'success',
                title: `Member ${targetEnable ? 'enabled' : 'disabled'} successfully!`,
            })

        } catch (error) {
            setOptimisticToggles(prev => {
                const newState = { ...prev }
                delete newState[user.id]
                return newState
            })
            console.error(`Error updating member status:`, error)
            Toast.fire({
                icon: 'error',
                title: error.message || `Failed to update member status`,
            })
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-[24px] md:text-[32px] font-semibold text-[var(--Dark-Blue---Primary,#2b364b)] font-barlowcondensed uppercase">
                    Team Members
                </h2>
                {!isSaddlefitAdmin && (
                    <Button
                        onClick={() => onOpenModal()}
                        className="create-saddle-btn !w-auto !h-[40px] px-6 cursor-pointer !py-0 flex items-center justify-center"
                        AppendIcon={FaUserPlus}
                    >
                        Add Member
                    </Button>
                )}
            </div>

            <div className="overflow-x-auto min-h-[200px] flex flex-col">
                {usersLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <BeatLoader color={'#2b364b'} />
                    </div>
                ) : usersError ? (
                    <div className="text-center py-12 text-red-500 font-montserrat">
                        Error loading team members: {usersError.message}
                    </div>
                ) : teamMembers.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500 font-montserrat italic">
                            No team members added yet. Click "Add Member" to start building your team.
                        </p>
                    </div>
                ) : (
                    <table
                        className="min-w-full overflow-hidden"
                        style={{
                            border: '1px solid #999',
                            borderRadius: '10px',
                            borderSpacing: '0',
                            tableLayout: 'fixed',
                        }}
                    >
                        <colgroup>
                            <col style={{ width: isSaddlefitAdmin ? '50%' : '40%' }} />
                            <col style={{ width: isSaddlefitAdmin ? '25%' : '20%' }} />
                            <col style={{ width: isSaddlefitAdmin ? '25%' : '20%' }} />
                            {!isSaddlefitAdmin && <col style={{ width: '20%' }} />}
                        </colgroup>
                        <thead className="thead">
                            <tr className="table-head">
                                <th align="left" className="px-3 py-2" style={{ borderBottom: '1px solid #999' }}>User</th>
                                <th align="left" className="px-3 py-2" style={{ borderBottom: '1px solid #999' }}>Role</th>
                                <th align="left" className="px-3 py-2" style={{ borderBottom: '1px solid #999' }}>Status</th>
                                {!isSaddlefitAdmin && <th align="left" className="px-3 py-2" style={{ borderBottom: '1px solid #999' }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {teamMembers.map((user, index) => (
                                <tr key={user.id} className="hover:bg-blue-50 transition-colors">
                                    <td align="left" className="px-3 py-4 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
                                                {user.role === 'Admin' ? <FaUserShield /> : <FaUser />}
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900 font-montserrat">
                                                    {user.firstName} {user.lastName}
                                                </div>
                                                <div className="text-sm text-gray-500 font-montserrat">
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td align="left" className="px-3 py-4 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'Admin'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-blue-100 text-blue-800'
                                            } font-montserrat`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td align="left" className="px-3 py-4 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isEnable
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            } font-montserrat`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    {!isSaddlefitAdmin && (
                                        <td align="left" className="px-3 py-4 whitespace-nowrap table-order" style={{ borderBottom: '1px solid #999' }}>
                                            <div className="flex items-center gap-4 ml-4">
                                                <button
                                                    onClick={() => onOpenModal(user)}
                                                    className="text-gray-600 hover:text-[#2b364b] transition-colors cursor-pointer border-none bg-transparent flex items-center justify-center"
                                                    title="Edit Member"
                                                >
                                                    <FaEdit size={18} />
                                                </button>
                                                {user.role !== 'Admin' ? (
                                                    <div className="flex items-center gap-3">
                                                        <CustomToggle
                                                            checked={user.isEnable}
                                                            onChange={() => handleToggleUserStatus(user)}
                                                        />
                                                        <span className={`text-[11px] font-bold uppercase tracking-wider ${user.isEnable ? 'text-green-600' : 'text-gray-400'}`}>
                                                            {user.isEnable ? 'Active' : 'Disabled'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="w-[18px]" />
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {isModalOpen && (
                <ModalComponent
                    title={isEditMode ? "Edit Team Member" : "Add Team Member"}
                    setOpenModal={onCloseModal}
                    openEditModal={isEditMode}
                    setOpenEditModal={onCloseModal}
                    onCreate={handleSubmit(onSubmit)}
                    onUpdate={handleSubmit(onSubmit)}
                    createLoading={!isEditMode && submitLoading}
                    updateLoading={isEditMode && submitLoading}
                >
                    <div className="px-8 pt-4 pb-2">
                        <FormProvider {...methods}>
                            <form className="space-y-4">
                                <div className="grid grid-cols-1 gap-4">
                                    <FormInput
                                        name="firstName"
                                        label="First Name"
                                        placeholder="First Name"
                                        validations={{ required: "First name is required" }}
                                    />
                                    <FormInput
                                        name="lastName"
                                        label="Last Name"
                                        placeholder="Last Name"
                                        validations={{ required: "Last name is required" }}
                                    />
                                    <FormInput
                                        name="email"
                                        label="Email"
                                        placeholder="email@example.com"
                                        type="email"
                                        disabled={isEditMode}
                                        validations={{
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                                message: "Invalid email address"
                                            }
                                        }}
                                    />
                                </div>
                            </form>
                        </FormProvider>
                    </div>
                </ModalComponent>
            )}
        </div>
    )
}

export default ManufacturerUsers
